import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatMoney } from '@/lib/utils';

type Dashboard = {
  event: { id: number; name: string; isActive: boolean } | null;
  totals: { orders: number; revenue: string; customers: number };
  byLine: { lineCode: string; total: string; unites: number }[];
  byRep: { fullName: string; username: string; commandes: number; total: string }[];
  topProducts: {
    lineCode: string;
    partNumber: string;
    description: string;
    unites: number;
    total: string;
  }[];
  hourly: { heure: string; commandes: number; total: string }[];
  generatedAt: string;
};

/**
 * Dashboard temps réel — conçu pour être projeté sur écran géant (plan §5.7).
 * Auto-refresh 30 s ; grands chiffres, lisibles de loin.
 */
export default function Dashboard() {
  const { data, isPending, isError, dataUpdatedAt } = useQuery<Dashboard>({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => api('/api/admin/dashboard'),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  if (isPending) return <p className="text-muted-foreground p-8 text-center">Chargement…</p>;
  if (isError || !data.event) {
    return (
      <p className="text-muted-foreground p-8 text-center">
        Aucun événement à afficher. Activez un événement pour voir les ventes.
      </p>
    );
  }

  const maxHeure = Math.max(1, ...data.hourly.map((h) => Number(h.total)));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-xl font-bold">{data.event.name}</h2>
        <p className="text-muted-foreground text-xs">
          À jour · {new Date(dataUpdatedAt).toLocaleTimeString('fr-CA')} · rafraîchit toutes les 30 s
        </p>
      </div>

      {/* Grands chiffres */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Tuile label="Ventes du show" valeur={formatMoney(data.totals.revenue)} accent />
        <Tuile label="Commandes" valeur={String(data.totals.orders)} />
        <Tuile label="Clients servis" valeur={String(data.totals.customers)} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Carte titre="Par fournisseur">
          {data.byLine.length === 0 ? (
            <Vide />
          ) : (
            <ul className="space-y-2">
              {data.byLine.map((l) => (
                <li key={l.lineCode} className="flex items-center justify-between">
                  <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-sm font-medium">
                    {l.lineCode}
                  </span>
                  <span className="text-muted-foreground ml-2 flex-1 text-sm">{l.unites} unités</span>
                  <span className="font-semibold tabular-nums">{formatMoney(l.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Carte>

        <Carte titre="Par représentant">
          {data.byRep.length === 0 ? (
            <Vide />
          ) : (
            <ul className="space-y-2">
              {data.byRep.map((r) => (
                <li key={r.username} className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate">{r.fullName}</span>
                  <span className="text-muted-foreground text-sm">{r.commandes}</span>
                  <span className="font-semibold tabular-nums">{formatMoney(r.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Carte>
      </div>

      <Carte titre="Ventes par heure">
        {data.hourly.length === 0 ? (
          <Vide />
        ) : (
          <div className="flex items-end gap-2" style={{ height: 160 }}>
            {data.hourly.map((h) => (
              <div key={h.heure} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-muted-foreground text-xs tabular-nums">{formatMoney(h.total)}</span>
                <div
                  className="bg-primary w-full rounded-t"
                  style={{ height: `${Math.max(4, (Number(h.total) / maxHeure) * 120)}px` }}
                  title={`${h.heure} · ${h.commandes} commande(s)`}
                />
                <span className="text-muted-foreground text-xs">{h.heure.slice(11, 16)}</span>
              </div>
            ))}
          </div>
        )}
      </Carte>

      <Carte titre="Top 10 produits">
        {data.topProducts.length === 0 ? (
          <Vide />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-left">
                <th className="pb-2 font-medium">Pièce</th>
                <th className="pb-2 font-medium">Description</th>
                <th className="pb-2 text-right font-medium">Unités</th>
                <th className="pb-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.topProducts.map((p) => (
                <tr key={`${p.lineCode}-${p.partNumber}`} className="border-t">
                  <td className="py-2 font-mono text-xs">
                    <span className="text-muted-foreground">{p.lineCode}</span> {p.partNumber}
                  </td>
                  <td className="py-2">{p.description}</td>
                  <td className="py-2 text-right tabular-nums">{p.unites}</td>
                  <td className="py-2 text-right font-medium tabular-nums">{formatMoney(p.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Carte>
    </div>
  );
}

function Tuile({ label, valeur, accent }: { label: string; valeur: string; accent?: boolean }) {
  return (
    <div className={`rounded-xl border p-6 ${accent ? 'bg-primary text-primary-foreground' : 'bg-card'}`}>
      <p className={accent ? 'text-primary-foreground/80 text-sm' : 'text-muted-foreground text-sm'}>
        {label}
      </p>
      <p className="mt-1 text-4xl font-bold tabular-nums">{valeur}</p>
    </div>
  );
}

function Carte({ titre, children }: { titre: string; children: React.ReactNode }) {
  return (
    <section className="bg-card rounded-xl border p-6">
      <h3 className="mb-4 font-semibold">{titre}</h3>
      {children}
    </section>
  );
}

function Vide() {
  return <p className="text-muted-foreground text-sm">Aucune vente pour l'instant.</p>;
}
